import { Check, Loader2, Save, Trash2 } from 'lucide-react';
import type {
  HoraCertaQuarantineCargoOption,
  HoraCertaQuarantineDock,
  HoraCertaQuarantineRecord,
} from 'contracts/HoraCertaImport';
import {
  formatDuration,
  formatScheduledDate,
  formatScheduledTime,
  isScheduledDateInThePast,
  parseTimeToMinutes,
} from '../../helpers';
import type { RowSelection, SavedOrder, SlotOption } from '../../types';

interface QuarantineRowProps {
  record: HoraCertaQuarantineRecord;
  selection?: RowSelection;
  saved?: SavedOrder;
  docks: HoraCertaQuarantineDock[];
  isLoadingDocks: boolean;
  isSaving: boolean;
  isBusy: boolean;
  error?: string;
  standardDuration?: number | null;
  cargoOptions: HoraCertaQuarantineCargoOption[];
  slotOptions: SlotOption[];
  onCargoTypeChange: (recordId: number, cargoTypeId: number) => void;
  onSlotChange: (recordId: number, slot: string) => void;
  onSave: (record: HoraCertaQuarantineRecord) => void;
  onDelete: (record: HoraCertaQuarantineRecord) => void;
}

export const QuarantineRow = ({
  record,
  selection,
  saved,
  docks,
  isLoadingDocks,
  isSaving,
  isBusy,
  error,
  standardDuration,
  cargoOptions,
  slotOptions,
  onCargoTypeChange,
  onSlotChange,
  onSave,
  onDelete,
}: QuarantineRowProps) => {
  const filled = Boolean(
    selection?.date && selection?.cargoTypeId && selection?.slot
  );
  const isPastDate = isScheduledDateInThePast(record.originalScheduledAt);
  const selectedTaken = Boolean(
    selection?.slot &&
      slotOptions.find((option) => option.value === selection.slot)?.taken
  );

  const selectedDockId = selection?.slot
    ? Number(selection.slot.split('::')[0])
    : null;
  const selectedDock = selectedDockId
    ? docks.find((entry) => entry.dock.id === selectedDockId)
    : undefined;
  const originalMinutes = record.originalScheduledAt
    ? parseTimeToMinutes(formatScheduledTime(record.originalScheduledAt))
    : null;
  const dockStart = selectedDock
    ? parseTimeToMinutes(selectedDock.dock.startTime)
    : null;
  const dockEnd = selectedDock
    ? parseTimeToMinutes(selectedDock.dock.endTime)
    : null;
  const outsideDockWindow =
    originalMinutes !== null &&
    dockStart !== null &&
    dockEnd !== null &&
    (originalMinutes < dockStart || originalMinutes >= dockEnd);
  const showConflict = selectedTaken || outsideDockWindow;

  return (
    <tr className="hover:bg-slate-50/60">
      <td
        className="sticky left-0 z-[1] bg-white py-3 px-6 whitespace-nowrap"
        style={{ boxShadow: '4px 0 8px -6px rgba(15, 23, 42, 0.14)' }}
      >
        <span className="font-mono font-semibold text-slate-800">
          {record.thirdOrder}
        </span>
        <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
          HC
        </span>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 -right-2 w-2"
          style={{
            background:
              'linear-gradient(to right, rgba(15, 23, 42, 0.09), transparent)',
          }}
        />
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.originalScheduledAt ? (
          <>
            <span>{formatScheduledDate(record.originalScheduledAt)}</span>
            <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
              HC
            </span>
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.originalScheduledAt ? (
          <>
            <span>{formatScheduledTime(record.originalScheduledAt)}</span>
            <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
              HC
            </span>
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.warehouse.name}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.carrier.name}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.company.name}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.orderType.name}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.operationType.name}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.weightCapacity.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.driverName}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.driverDocument}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.cellPhone || '—'}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.vehiclePlate}
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {record.vehicleType.name}
      </td>
      <td className="max-w-xs py-3 px-6 text-slate-600">
        {record.observation || '—'}
      </td>
      <td className="py-3 px-6">
        <select
          aria-label={`Tipo de carga do agendamento ${record.thirdOrder}`}
          className="w-full max-w-sm text-sm font-sans border border-slate-300 rounded-md py-1.5 px-2 disabled:bg-slate-50 disabled:text-slate-400"
          value={selection?.cargoTypeId ?? ''}
          disabled={Boolean(saved) || cargoOptions.length === 0}
          onChange={(event) =>
            onCargoTypeChange(record.id, Number(event.target.value))
          }
        >
          <option value="">
            {cargoOptions.length === 0
              ? 'Sem Setup de veículos'
              : 'Selecione o tipo de carga...'}
          </option>
          {cargoOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </td>
      <td className="py-3 px-6 whitespace-nowrap text-slate-600">
        {formatDuration(standardDuration)}
      </td>
      <td className="py-3 px-6">
        <select
          aria-label={`Doca do agendamento ${record.thirdOrder}`}
          className="w-full max-w-sm text-sm font-sans border border-slate-300 rounded-md py-1.5 px-2 disabled:bg-slate-50 disabled:text-slate-400"
          value={selection?.slot ?? ''}
          disabled={
            Boolean(saved) ||
            !selection?.date ||
            !selection?.cargoTypeId ||
            isLoadingDocks
          }
          onChange={(event) => onSlotChange(record.id, event.target.value)}
        >
          <option value="">
            {isLoadingDocks
              ? 'Carregando horários...'
              : 'Selecione uma doca...'}
          </option>
          {slotOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.taken}
            >
              {option.label}
              {option.taken ? ' (indisponível)' : ''}
            </option>
          ))}
        </select>
        {selection?.date &&
          selection?.cargoTypeId &&
          !isLoadingDocks &&
          docks.length === 0 && (
          <p className="mt-1 text-[11px] text-amber-600">
            {isPastDate
              ? 'A data/hora original já passou. Não há horários disponíveis.'
              : 'Nenhum horário disponível nesta data.'}
          </p>
        )}
        {showConflict && !error && (
          <p className="mt-1 text-[11px] text-red-600">Conflito de horario</p>
        )}
        {error && (
          <p role="alert" className="mt-1 text-[11px] text-red-600">
            {error}
          </p>
        )}
      </td>
      <td className="py-3 px-6 text-center">
        <button
          type="button"
          aria-label={`Salvar agendamento ${record.thirdOrder}`}
          title={saved ? 'Agendamento salvo' : 'Salvar agendamento'}
          disabled={!filled || Boolean(saved) || isBusy}
          onClick={() => onSave(record)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EE7C2C] disabled:opacity-40"
        >
          {saved ? (
            <Check size={16} aria-hidden="true" />
          ) : isSaving ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Save size={16} aria-hidden="true" />
          )}
        </button>
      </td>
      <td className="py-3 px-6 text-center">
        {!saved ? (
          <button
            type="button"
            aria-label={`Excluir agendamento ${record.thirdOrder} da quarentena`}
            title="Excluir da quarentena"
            disabled={isBusy}
            onClick={() => onDelete(record)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:opacity-40"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        ) : null}
      </td>
    </tr>
  );
};
